import { useRef, useState } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "@/components/Avatar";
import { api, errorMessage } from "@/lib/api";
import { uploadImage } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";

export function AvatarUpload({ url, name, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();

  const save = async (newUrl) => {
    const { data } = await api.patch("/auth/avatar", { url: newUrl });
    setUser(data);
    onChange(newUrl);
  };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await save(await uploadImage(api, file));
      toast.success("Profile picture updated");
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async () => {
    try { await save(""); toast.success("Profile picture removed"); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="relative group shrink-0">
      <button type="button" data-testid="profile-avatar-upload" onClick={() => ref.current?.click()} disabled={busy}
        className="relative h-20 w-20 rounded-3xl overflow-hidden block" aria-label="Change profile picture">
        <Avatar url={url} name={name} size="h-20 w-20" text="text-3xl" className="!rounded-3xl" testid="profile-avatar" />
        <span className={`absolute inset-0 bg-[#0B0F19]/60 grid place-items-center transition-opacity ${busy ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
        </span>
      </button>
      <span className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-[#FFB800] text-[#0B0F19] grid place-items-center pointer-events-none border-2 border-[#0B0F19]"><Camera className="h-3.5 w-3.5" /></span>
      {url && (
        <button type="button" data-testid="profile-avatar-remove" onClick={remove} className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-[#151C2C] border border-[#26334D] grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Remove photo">
          <Trash2 className="h-3 w-3 text-[#FF7A7A]" />
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" hidden data-testid="profile-avatar-input" onChange={pick} />
    </div>
  );
}
